import React, { useEffect, useRef, useState } from 'react';
import { Card, Input, Fieldset, Button, DateInput, InsetText, Textarea, Select, Checkboxes, Radios } from 'nhsuk-react-components';
import Callout from '../../callout/Callout.component';
import SelectOther from '../../selectOther/SelectOther.component';
import ISODateInput from '../../date/ISODateInput.component';
import InputBarcodeScanner from './InputBarcodeScanner';
import ImplantEntitySelect from '../../implantEntitySelect/ImplantEntitySelect.component';

const EditImplantModal = ({
    updatedImplantInstance,
    setUpdatedImplantInstance,
    handleUpdateImplantInstance,
    implantInstanceUpdateProcessing,
    successCreate,
    successUpdate,
    implantInstanceCreateErrors,
    implantInstanceUpdateErrors,
    loadingImplantDetailsByBarcode,
    handleGetImplantByBarcode,
    scannedBarcode,
    setScannedBarcode,
    modelNotFound,
    autoFillDetail,
    isGenerator,
    isLead,
    onClose
}) => {
    const pendingAction = useRef(null);
    const [searchMethod, setSearchMethod] = useState('barcode'); // 'barcode' or 'search'
    const [selectedImplant, setSelectedImplant] = useState(null);

    // Handle success actions for edit modal
    useEffect(() => {
        if (successUpdate && pendingAction.current === 'save') {
            // Optional: Close modal on successful update if onClose is provided
            if (typeof onClose === 'function') {
                onClose();
            }
            pendingAction.current = null; // Reset after handling
        }
    }, [successUpdate, onClose]);
    return (
        <Card id='edit-card'>
            <Card.Content>
                <Card.Heading className="nhsuk-heading-m">
                    Edit Implant
                </Card.Heading>

                <Card.Description>
                    <ISODateInput
                        id="updateEventDateTime"
                        label="Implantation Date"
                        name="eventDateTime"
                        value={updatedImplantInstance?.eventDate}
                        onChange={(dt) => setUpdatedImplantInstance({ ...updatedImplantInstance, eventDate: dt })}
                        required
                    />

                    <Fieldset>
                        <Fieldset.Legend>Search Method</Fieldset.Legend>
                        <Radios name="searchMethod" id="search-method">
                            <Radios.Radio
                                value="barcode"
                                checked={searchMethod === 'barcode'}
                                onChange={(e) => setSearchMethod(e.target.value)}
                            >
                                Barcode Scanner
                            </Radios.Radio>
                            <Radios.Radio
                                value="search"
                                checked={searchMethod === 'search'}
                                onChange={(e) => setSearchMethod(e.target.value)}
                            >
                                Search Implant Database
                            </Radios.Radio>
                        </Radios>
                    </Fieldset>

                    {searchMethod === 'barcode' ? (
                        <InsetText>
                            <InputBarcodeScanner
                                value={scannedBarcode}
                                onChange={(value) => {
                                    const barcodeValue = typeof value === 'object' ? value.target.value : value;
                                    setScannedBarcode(barcodeValue);
                                }}
                                onSearch={(value) => {
                                    if (autoFillDetail) {
                                        handleGetImplantByBarcode(value);
                                    }
                                }}
                                label="Barcode/GTIN"
                                name="barcode"
                                type="text"
                                required
                            />
                            {autoFillDetail && (
                                <Button
                                    disabled={loadingImplantDetailsByBarcode}
                                    onClick={() => handleGetImplantByBarcode(scannedBarcode)}
                                >
                                    {loadingImplantDetailsByBarcode ? 'Loading...' : 'Auto-check details'}
                                </Button>
                            )}

                            {modelNotFound && (
                                <p style={{ color: 'red', marginTop: '10px' }}>Model not found in the database. Please enter details manually.</p>
                            )}
                        </InsetText>
                    ) : (
                        <InsetText>
                            <ImplantEntitySelect
                                selectedId={selectedImplant?.id || updatedImplantInstance?.barcode ? null : null}
                                onChange={(implant) => {
                                    if (implant) {
                                        setSelectedImplant(implant);
                                        setUpdatedImplantInstance({
                                            ...updatedImplantInstance,
                                            barcode: implant.barcode || '',
                                            manufacturer: implant.manufacturer || '',
                                            modelName: implant.modelName || '',
                                            modelNumber: implant.modelNumber || '',
                                            modelCategory: implant.modelCategory || '',
                                            mrConditional: implant.mrConditional || false,
                                        });
                                    } else {
                                        setSelectedImplant(null);
                                    }
                                }}
                                title="Select Implant from Database"
                                enableCreate={true}
                            />
                        </InsetText>
                    )}

                    <Input
                        id="manufacturer"
                        label="Manufacturer"
                        name="manufacturer"
                        type="text"
                        onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, manufacturer: e.target.value })}
                        value={updatedImplantInstance?.manufacturer}
                        required
                    />

                    <Input
                        id="modelName"
                        label="Model Name"
                        name="modelName"
                        type="text"
                        onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, modelName: e.target.value })}
                        value={updatedImplantInstance?.modelName}
                        required
                    />

                    <Input
                        id="modelNumber"
                        label="Model Number"
                        name="modelNumber"
                        type="text"
                        onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, modelNumber: e.target.value })}
                        value={updatedImplantInstance?.modelNumber}
                        required
                    />

                    <Input
                        id="barcode"
                        label="GTIN"
                        name="barcode"
                        type="text"
                        onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, barcode: e.target.value })}
                        value={updatedImplantInstance?.barcode}
                        required
                    />

                    <Input
                        id="serialNumber"
                        label="Serial Number"
                        name="serialNumber"
                        type="text"
                        onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, serialNumber: e.target.value })}
                        value={updatedImplantInstance?.serialNumber}
                        required
                    />

                    <InsetText>
                        <Select
                            id="modelCategory"
                            label="Model Category"
                            name="modelCategory"
                            onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, modelCategory: e.target.value })}
                            value={updatedImplantInstance?.modelCategory}
                            required
                        >
                            <Select.Option value="">Select a model category</Select.Option>
                            <Select.Option value="Accessory">Accessory</Select.Option>
                            <Select.Option value="Generator">Generator</Select.Option>
                            <Select.Option value="Lead">Lead</Select.Option>
                            <Select.Option value="Implantable Monitor">Implantable Monitor</Select.Option>
                        </Select>

                        {isGenerator && (
                            <div>
                                <SelectOther
                                    id="generatorImplantSite"
                                    label="Generator Implant Site"
                                    name="generatorImplantSite"
                                    onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, generatorImplantSite: e.target.value })}
                                    value={updatedImplantInstance?.generatorImplantSite}
                                >
                                    <Select.Option value="">Select a generator implant site</Select.Option>
                                    <Select.Option value="Chest wall - subcutaneous">Chest wall - subcutaneous</Select.Option>
                                    <Select.Option value="Chest wall - submuscular">Chest wall - submuscular</Select.Option>
                                    <Select.Option value="Abdominal - subcutaneous">Abdominal - subcutaneous</Select.Option>
                                    <Select.Option value="Abdominal - submuscular">Abdominal - submuscular</Select.Option>
                                    <Select.Option value="Intravascular - endocardial">Intravascular - endocardial</Select.Option>
                                    <Select.Option value="Intravascular - pulmonary artery">Intravascular - pulmonary artery</Select.Option>
                                </SelectOther>

                                <Select
                                    id="maxSystemCapability"
                                    label="Max System Capability"
                                    name="maxSystemCapability"
                                    onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, maxSystemCapability: e.target.value })}
                                    value={updatedImplantInstance?.maxSystemCapability}
                                >
                                    <Select.Option value="">Select a max system capability</Select.Option>
                                    <Select.Option value="PM-AAI(R)">PM-AAI(R)</Select.Option>
                                    <Select.Option value="CRTD">CRTD</Select.Option>
                                    <Select.Option value="CRTP">CRTP</Select.Option>
                                    <Select.Option value="PM-DDD(R)">PM-DDD(R)</Select.Option>
                                    <Select.Option value="ICD-DR">ICD-DR</Select.Option>
                                    <Select.Option value="ICD-SQ">ICD-SQ</Select.Option>
                                    <Select.Option value="ICD-VR">ICD-VR</Select.Option>
                                    <Select.Option value="Monitor">Monitor</Select.Option>
                                    <Select.Option value="None">None</Select.Option>
                                    <Select.Option value="Other">Other</Select.Option>
                                    <Select.Option value="PM-VVI(R)">PM-VVI(R)</Select.Option>
                                    <Select.Option value="PM-VDD(R) - single lead">PM-VDD(R) - single lead</Select.Option>
                                    <Select.Option value="ICD -VDD(R) - single lead">ICD -VDD(R) - single lead</Select.Option>
                                </Select>
                            </div>
                        )}

                        {isLead && (
                            <div>
                                <Select
                                    id="leadChamber"
                                    label="Lead Chamber"
                                    name="leadChamber"
                                    onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, leadChamber: e.target.value })}
                                    value={updatedImplantInstance?.leadChamber}
                                >
                                    <Select.Option value="">Select a lead chamber</Select.Option>
                                    <Select.Option value="Right Atrium">Right Atrium</Select.Option>
                                    <Select.Option value="Right Ventricle">Right Ventricle</Select.Option>
                                    <Select.Option value="Left Ventricle">Left Ventricle</Select.Option>
                                    <Select.Option value="NA">NA</Select.Option>
                                </Select>

                                <SelectOther
                                    id="pacingSite"
                                    label="Pacing Site"
                                    name="pacingSite"
                                    onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, pacingSite: e.target.value })}
                                    value={updatedImplantInstance?.pacingSite}
                                >
                                    <Select.Option value="">Select a pacing site</Select.Option>
                                    <Select.Option value="Atrial other">Atrial other</Select.Option>
                                    <Select.Option value="CS - Anterior branch">CS - Anterior branch</Select.Option>
                                    <Select.Option value="CS - Anterolateral branch">CS - Anterolateral branch</Select.Option>
                                    <Select.Option value="CS - Lateral branch">CS - Lateral branch</Select.Option>
                                    <Select.Option value="CS - Posterior branch">CS - Posterior branch</Select.Option>
                                    <Select.Option value="CS - Posterolateral branch">CS - Posterolateral branch</Select.Option>
                                    <Select.Option value="Epicardial (surgical)">Epicardial (surgical)</Select.Option>
                                    <Select.Option value="Greater cardiac vein">Greater cardiac vein</Select.Option>
                                    <Select.Option value="His/Conduction system">His/Conduction system</Select.Option>
                                    <Select.Option value="LA via CS">LA via CS</Select.Option>
                                    <Select.Option value="LV endocardial - trans-septal">LV endocardial - trans-septal</Select.Option>
                                    <Select.Option value="LV epicardial - via pericardium">LV epicardial - via pericardium</Select.Option>
                                    <Select.Option value="LV other">LV other</Select.Option>
                                    <Select.Option value="Middle cardiac vein">Middle cardiac vein</Select.Option>
                                    <Select.Option value="RA endocardial">RA endocardial</Select.Option>
                                    <Select.Option value="RV apex endocardial">RV apex endocardial</Select.Option>
                                    <Select.Option value="RV epicardial (surgical)">RV epicardial (surgical)</Select.Option>
                                    <Select.Option value="RV other">RV other</Select.Option>
                                    <Select.Option value="RV outflow endocardial">RV outflow endocardial</Select.Option>
                                    <Select.Option value="RV septal">RV septal</Select.Option>
                                </SelectOther>
                            </div>
                        )}
                    </InsetText>

                    <Select
                        id="status"
                        label="Status"
                        name="status"
                        onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, status: e.target.value })}
                        value={updatedImplantInstance?.status}
                        required
                    >
                        <Select.Option value="">Select a status</Select.Option>
                        <Select.Option value="Implanted, connected">Implanted, connected</Select.Option>
                        <Select.Option value="Explanted">Explanted</Select.Option>
                        <Select.Option value="Partial extraction">Partial extraction</Select.Option>
                        <Select.Option value="Complete extraction">Complete extraction</Select.Option>
                        <Select.Option value="Implanted, abandoned">Implanted, abandoned</Select.Option>
                    </Select>


                    <ISODateInput
                        id="expiryDate"
                        label="Expiry Date"
                        name="expiryDate"
                        value={updatedImplantInstance?.expiryDate}
                        onChange={(dt) => setUpdatedImplantInstance({ ...updatedImplantInstance, expiryDate: dt })}
                        required
                    />

                    <Checkboxes name="checkboxes">
                        <Checkboxes.Box
                            checked={updatedImplantInstance?.abandonedHardware}
                            onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, abandonedHardware: e.target.checked })}
                        >
                            Abandoned Hardware
                        </Checkboxes.Box>
                        {/*
                        <Checkboxes.Box
                            checked={updatedImplantInstance?.mrConditional}
                            onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, mrConditional: e.target.checked })}
                        >
                            MRI Conditional
                        </Checkboxes.Box>
                        */}
                    </Checkboxes>

                    <Textarea
                        id="eventDetails"
                        label="Event Details"
                        name="eventDetails"
                        onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, eventDetails: e.target.value })}
                        value={updatedImplantInstance?.eventDetails}
                    />

                    <Textarea
                        id="PractitionerDetails"
                        label="Practitioner Details"
                        name="PractitionerDetails"
                        onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, practitionerDetails: e.target.value })}
                        value={updatedImplantInstance?.practitionerDetails}
                    />

                    <Textarea
                        id="Notes"
                        label="Notes"
                        name="Notes"
                        onChange={(e) => setUpdatedImplantInstance({ ...updatedImplantInstance, notes: e.target.value })}
                        value={updatedImplantInstance?.notes}
                    />

                    {(successCreate || successUpdate) && (
                        <Callout
                            label="Success"
                            header="Success!"
                            autoDismiss={true}
                            dismissTime={5000}
                            time={new Date().toLocaleTimeString()}
                            title="Success!"
                            message={successCreate ? "The implantInstance was created successfully." : "The implantInstance was updated successfully."}
                            mode="success"
                        />
                    )}

                    {(implantInstanceCreateErrors || implantInstanceUpdateErrors) && (
                        <Callout
                            label="Error"
                            header="Error!"
                            autoDismiss={true}
                            dismissTime={5000}
                            time={new Date().toLocaleTimeString()}
                            title="Error!"
                            message={"There was an error creating the implantInstance. Please try again."}
                            mode="error"
                        />
                    )}
                </Card.Description>

                <Button
                    disabled={implantInstanceUpdateProcessing}
                    onClick={() => {
                        pendingAction.current = 'save';
                        handleUpdateImplantInstance();
                    }}
                >
                    Save changes
                </Button>
            </Card.Content>
        </Card>
    );
};

export default EditImplantModal; 